import Image from 'next/image';
import { FC } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  priority?: boolean;
  sizes?: string;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  className?: string;
  // Add newer props for styling
  fill?: boolean;
  style?: React.CSSProperties;
}

/**
 * Renders an optimized image using the Next.js Image component with modern API.
 *
 * @param {OptimizedImageProps} props - The properties for the image.
 * @returns {JSX.Element} The optimized image component.
 */
const OptimizedImage: FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  quality = 75,
  priority = false,
  sizes,
  placeholder,
  blurDataURL,
  className,
  fill = false,
  style,
  ...rest
}) => {
  // Handle the fill case (previously layout="fill")
  if (fill) {
    return (
      <div className={cn("relative w-full h-full", className)}>
        <Image
          src={src}
          alt={alt}
          fill={true}
          style={{ objectFit: 'cover', ...style }}
          quality={quality}
          priority={priority}
          sizes={sizes || '100vw'} // Default size for fill images
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          {...rest}
        />
      </div>
    );
  }

  // Handle normal images (previously layout="responsive", "fixed", "intrinsic")
  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      quality={quality}
      priority={priority}
      sizes={sizes}
      placeholder={placeholder}
      blurDataURL={blurDataURL}
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default OptimizedImage;

// Example Usage (in another component):
/*
import OptimizedImage from './OptimizedImage';

const MyPageComponent = () => {
  return (
    <div>
      <h2>My Awesome Image</h2>
      <div style={{ width: '100%', maxWidth: '600px' }}>
        <OptimizedImage
          src="/images/my-sample-image.jpg" // Assuming image is in /public/images/
          alt="A descriptive alt text for the image"
          width={1600} // Original width of the image (aspect ratio w/h = 16/9)
          height={900} // Original height of the image
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          placeholder="blur"
          blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F9PQAI8wNPvd7POQAAAABJRU5ErkJggg==" // Example tiny blur image
        />
      </div>

      <h2>Image with Fill Layout</h2>
      <div style={{ position: 'relative', width: '300px', height: '200px', border: '1px solid black' }}>
        <OptimizedImage
          src="/images/another-image.jpg"
          alt="Another image filling its container"
          fill={true}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};

export default MyPageComponent;

*/
